<template>
  <!-- Load reference data and needs analysis data -->
  <ApiLoading v-if="isReferenceDataLoading || isReferenceDataFail" :apiError="isReferenceDataFail" />
  <main id="main-content" v-else>
    <div class="content-page-banner">
      <div class="banner-content">
        <h1 v-if="isReferenceDataFail || isReferenceDataLoading">Change my product</h1>
        <template v-else>
          <h1>Change my product</h1>
          <div class="underline"></div>
          <div v-html="refData.changeMyProductSubtitle.informationText" />
        </template>
      </div>
    </div>
    <Alert v-if="apiStatus.getMyRecommendations.fail || apiStatus.getPrice.fail" alertType="error">
      <p>It looks like we are experiencing a problem. Please try again.</p>
    </Alert>
      <div id="needs-analysis">
        <section id="multi-step-form" v-if="currentPage !== steps.MY_RECOMMENDATIONS.pageNumber">
          <!-- Form navigation -->
          <MultiStepFormNavigation :currentPage="currentPage" :links="navLinks" @linkClicked="updateStep" />
          <div class="form-content">
            <!-- Change my cover form -->
            <keep-alive>
              <component
                :is="currentPageView"
                @responseUpdated="updateVuexFormResponse"
                @navigatePage="updateCurrentPage"
                :refData="refData"
                :prefillDetails="getInitialCRMValues"
              />
            </keep-alive>
            <div class="form-page-nav">
              <section>
                <button v-if="currentPage !== steps.MY_RECOMMENDATIONS.pageNumber" @click.prevent="handleNextButton" :disabled="isNextButtonDisabled">{{ getNextBtnLabel }}</button>
              </section>
            </div>
          </div>
        </section>
        <section class="my-recommendations" v-else>
          <MyRecommendations
            v-if="currentPage === steps.MY_RECOMMENDATIONS.pageNumber"
            :refData="refData"
            :apiStatus="apiStatus"
            :fullRecommendationList="fullRecommendationList"
            :hospitalNeeds="hospitalNeeds"
            :extrasNeeds="extrasNeeds"
            :flexiFeatures="flexiFeatures"
            :excessValues="selectedExcessValues"
            @navigatePage="updateCurrentPage"
            @excessValuesUpdated="updateExcessValues"
            @changeMyCover="selectThisCover"
            :maxQuoteNum="fullRecommendationList.length"
            :needAnalysisData="changeCoverEnteredData"
          ></MyRecommendations>
        </section>

        <section v-if="currentPage === steps.MY_RECOMMENDATIONS.pageNumber">
          <div id="recommendations-legend" class="recommendations-legend">
            <div id="icons-row" class="icons-row">
              <div class="icon-key">
                <svg class="aux-icons">
                  <use xlink:href="/assets/public/images/icons.svg#included"></use>
                </svg>
                <p>Included</p>
              </div>
              <div class="icon-key">
                <svg class="aux-icons">
                  <use xlink:href="/assets/public/images/icons.svg#excluded"></use>
                </svg>
                <p>Excluded</p>
              </div>
              <div class="icon-key">
                <svg class="aux-icons">
                  <use xlink:href="/assets/public/images/icons.svg#restricted"></use>
                </svg>
                <p>Restricted</p>
              </div>
            </div>
            <section v-html="refData.recommendationsFooterText.informationText" v-if="currentPage === steps.MY_RECOMMENDATIONS.pageNumber" />
          </div>
        </section>
      </div>
  </main>
</template>

<script>
import { cloneDeep, isEmpty } from 'lodash';
import { subDays } from 'date-fns';
import Alert from '@/components/Alert.vue';
import { MultiStepFormNavigation, ApiLoading, Breadcrumb } from '@/components';
import { CoverComposition, CoverTypeValue, ProductType, NeedsAnalysisEligibility } from '@general/js/enums';
import PriceFactorStatement from '@general/js/components/PriceFactorStatement.vue';
import { DHLError, formatFormDate, getCurrentDate, formatDate } from '@general/js/utils';
import { getPartnerDetailsMP, sortListBasedOnAnotherList } from '@/utils';
import { findPolicyHolder, findSpouse } from '@/utils/ChangeProduct/ExtraUtils';
import Services from '@/services/MyCover/ChangeCover/ChangeProduct';
import MemberDetailsServices from '@/services/MemberDetails';
import { ChangeCover as ChangeCoverServices } from '@/services/MyCover';
import { PRODUCT_SELECTION } from '@/router/routeNames';
import { VeteranCardTypeOptions, RebateTierCode } from '@/enums';
import { formatQuoteLHCABD } from '@/utils/ChangeProduct/GetPremiumPrice/PersonUtils';
import { MyDetails, MyCover, MyNeeds, MyRecommendations } from './ChangeCoverSteps';
import ChangeCoverMixins from './ChangeCoverMixins';
import CaptureInformationMixins from './CaptureInformationMixins';

export default {
  name: 'Home',

  mixins: [ChangeCoverMixins, CaptureInformationMixins],

  components: {
    Alert,
    MultiStepFormNavigation,
    ApiLoading,
    Breadcrumb,
    MyDetails,
    MyCover,
    MyNeeds,
    MyRecommendations,
    PriceFactorStatement,
  },

  props: {
    // Prop to determine if user is coming from new cover selection page (by clicking "Back to my recommendations" link)
    fromCoverSelectionPage: {
      type: Boolean,
      default: false,
    },

    stepName: {
      type: String,
    },

    coverDetails: {
      type: Object,
    },
  },

  created() {
    if (!this.fromCoverSelectionPage) {
      // Reset vuex only if user is not coming from new cover selection page
      this.resetData();
      this.resetChangeCoverData();
    }
    this.resetCAIData();

    if (this.stepName === this.steps.MY_RECOMMENDATIONS.component) {
      this.updateStep(this.steps.MY_RECOMMENDATIONS.pageNumber);
    }

    if (this.stepName === this.steps.MY_DETAILS.component) {
      this.updateStep(this.steps.MY_DETAILS.pageNumber);
    }

    if (this.stepName === this.steps.MY_COVER.component) {
      this.updateStep(this.steps.MY_COVER.pageNumber);
    }

    if (this.stepName === this.steps.MY_NEEDS.component) {
      this.updateStep(this.steps.MY_NEEDS.pageNumber);
    }
  },

  async beforeMount() {
    await this.fetchData();
    await this.fetchMembershipDetails();
    await this.fetchProductHistory();
  },

  mounted() {
    // getInitialCRMValues defaults to true in data() - no longer needs route params check
  },

  data() {
    return {
      refData: null,
      apiStatus: {
        refData: {
          loading: false,
          fail: false,
        },
        membershipDetails: {
          loading: false,
          fail: false,
        },
        getMyRecommendations: {
          loading: false,
          fail: false,
        },
        getPrice: {
          loading: false,
          fail: false,
        },
        productHistory: {
          loading: false,
          fail: false,
        },
      },
      currentPage: 1,
      currentPageView: 'MyDetails',
      steps: {
        MY_DETAILS: {
          pageNumber: 1,
          title: 'My details',
          component: 'MyDetails',
          isInvalid: false,
        },
        MY_COVER: {
          pageNumber: 2,
          title: 'My cover',
          component: 'MyCover',
          isInvalid: true,
        },
        MY_NEEDS: {
          pageNumber: 3,
          title: 'My needs',
          component: 'MyNeeds',
          isInvalid: true,
        },
        MY_RECOMMENDATIONS: {
          pageNumber: 4,
          title: 'My recommendations',
          component: 'MyRecommendations',
          isInvalid: true,
        },
      },
      statementData: null,
      fullRecommendationList: [],
      hospitalNeeds: [],
      extrasNeeds: [],
      flexiFeatures: [],
      selectedExcessValues: [],
      getInitialCRMValues: true,
    };
  },

  computed: {
    /**
     * Computes whether the link is disabled using the validation that is completed in Vuelidate.
     */
    navLinks() {
      const disabledMyCover = this.steps.MY_DETAILS.isInvalid;
      const disabledMyNeeds = disabledMyCover || this.steps.MY_COVER.isInvalid;
      const disabledRecommendations = disabledMyNeeds || this.steps.MY_NEEDS.isInvalid;
      return [
        { linkTitle: this.steps.MY_DETAILS.title, pages: [1], disabled: false },
        { linkTitle: this.steps.MY_COVER.title, pages: [2], disabled: disabledMyCover },
        { linkTitle: this.steps.MY_NEEDS.title, pages: [3], disabled: disabledMyNeeds },
        { linkTitle: this.steps.MY_RECOMMENDATIONS.title, pages: [4], disabled: disabledRecommendations },
      ];
    },

    // Loading reference data and needs analysis data
    isReferenceDataLoading() {
      return this.apiStatus.membershipDetails.loading || this.apiStatus.refData.loading || this.apiStatus.productHistory.loading;
    },

    isReferenceDataFail() {
      return this.apiStatus.membershipDetails.fail || this.apiStatus.refData.fail || this.apiStatus.productHistory.fail;
    },

    isNextButtonDisabled() {
      switch (this.currentPage) {
        case this.steps.MY_DETAILS.pageNumber:
          return this.steps.MY_DETAILS.isInvalid;

        case this.steps.MY_COVER.pageNumber:
          return this.steps.MY_COVER.isInvalid;

        case this.steps.MY_NEEDS.pageNumber:
          return this.steps.MY_NEEDS.isInvalid;

        case this.steps.MY_RECOMMENDATIONS.pageNumber:
          return this.steps.MY_RECOMMENDATIONS.isInvalid;

        default:
          break;
      }
      return true;
    },

    getNextBtnLabel() {
      if (this.currentPage === this.steps.MY_NEEDS.pageNumber) {
        return this.refData.recommendationsButtonLabel.informationText;
      }
      return this.refData.nextButtonLabel.informationText;
    },
  },

  methods: {
    selectThisCover() {
      this.$router.push({ name: PRODUCT_SELECTION });
    },

    updateCurrentPage(targetPageName) {
      this.currentPageView = targetPageName;
      switch (targetPageName) {
        case this.steps.MY_DETAILS.component:
          this.currentPage = this.steps.MY_DETAILS.pageNumber;
          break;
        case this.steps.MY_COVER.component:
          this.currentPage = this.steps.MY_COVER.pageNumber;
          break;
        case this.steps.MY_NEEDS.component:
          this.currentPage = this.steps.MY_NEEDS.pageNumber;
          break;
        case this.steps.MY_RECOMMENDATIONS.component:
          this.currentPage = this.steps.MY_RECOMMENDATIONS.pageNumber;
          break;

        default:
          break;
      }
    },

    /**
     * Updates the current step/page number
     */
    updateStep(newPageNumber) {
      const currentStep = Object.values(this.steps).filter((page) => {
        return page.pageNumber === newPageNumber;
      });
      if (currentStep[0].pageNumber === this.steps.MY_RECOMMENDATIONS.pageNumber) {
        this.getInitialCRMValues = false;
        this.fetchRecommendedProducts(); // rerun fetch products whenever recommendations page is accessed
      }
      this.updateCurrentPage(currentStep[0].component);
    },

    /**
     * Allows for PriceFactorStatement to jump to correct step when a value is clicked
     */
    jumpToField(step) {
      this.updateStep(step);
    },

    /**
     * Updates the newly selected excess values
     * Reruns the price call for api whenever excess value changes
     */
    updateExcessValues(excessValues) {
      this.selectedExcessValues = excessValues;
      this.getPrice();
    },

    getInitialExcessValues() {
      if (this.fullRecommendationList) {
        this.fullRecommendationList.forEach((recommendation, index) => {
          const excessValue = this.findProductExcess(recommendation);
          // the current cover is always last
          if (index === this.fullRecommendationList.length - 1) {
            this.selectedExcessValues[index] = this.currentExcess.toString(); // take current excess value for current cover
          } else {
            this.selectedExcessValues[index] = excessValue ? excessValue[0].toString() : null;
          }
        });
      }
    },

    /**
     * Goes through the products of a given recommendation to find the product excess value list
     */
    findProductExcess(recommendation) {
      for (let i = 0; i < recommendation.products.length; i++) {
        const productType = recommendation.products[i].productType.toLowerCase();
        if (productType === ProductType.HOSPITAL_ONLY || productType === ProductType.PACKAGE) {
          return recommendation.products[i].productExcess;
        }
      }
      return null;
    },

    /**
     * Fetches data from different APIs
     */
    async fetchData() {
      await this.fetchChangeProductRefData(this.apiStatus.refData);
      await this.fetchMembershipDetailsAPI(this.apiStatus.membershipDetails, this.coverDetails);
    },

    /**
     * Fetch membership details OOTB API
     */
    async fetchMembershipDetails() {
      this.apiStatus.membershipDetails.loading = true;
      this.apiStatus.membershipDetails.fail = false;

      try {
        // Temp workaround to check if ph/partner is contributor on landing page
        const resMembershipDetails = await (this.coverDetails ? Promise.resolve({ ...this.coverDetails }) : Services.getMembershipDetails(this.memberNumber));
        // Get members in policy if members do not yet exist
        if (!this.members) {
          const allMembers = await MemberDetailsServices.getMembers(this.memberNumber);
          this.updateMembers(allMembers);
        }
        this.updateMemberDetails(resMembershipDetails.members);
        this.updateEligibilityDetails(resMembershipDetails.eligibility);
        this.getPolicyHolderInformation();

        this.updateMembershipProducts(resMembershipDetails.products);
        // checks if current policy is closed hospital and extras product
        this.getClosedHospitalProduct();
        this.getClosedExtrasProduct();

        this.updateMembershipPolicy(resMembershipDetails.membership);
        this.getInitialRebateTierLevel();

        // update fetched values into vuex store
        this.updateValuesIntoVuex();
      } catch (error) {
        this.apiStatus.membershipDetails.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.membershipDetails.loading = false;
      }
    },

    async fetchProductHistory() {
      this.apiStatus.productHistory.loading = true;
      this.apiStatus.productHistory.fail = false;

      try {
        if (this.memberDetails) {
          const policyHolder = findPolicyHolder(this.memberDetails);
          if (policyHolder) {
            const response = await ChangeCoverServices.fetchMemberProductHistory(this.memberNumber, policyHolder.personCode);
            this.updatePolicyHolderProductHistory(response.policies);
          }

          const partner = getPartnerDetailsMP(this.memberDetails, true);
          if (partner) {
            const response = await ChangeCoverServices.fetchMemberProductHistory(this.memberNumber, partner.personCode);
            this.updatePartnerProductHistory(response.policies);
          }
        }
      } catch (error) {
        this.apiStatus.productHistory.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.productHistory.loading = false;
      }
    },

    updateValuesIntoVuex() {
      this.updateCoverCompositionIntoVuex(); // fetches cover composition
      this.updateCurrentExcessIntoVuex(); // fetches current excess
      this.updatePaymentFrequencyIntoVuex();
    },

    /**
     * Fetch recommended products
     */
    async fetchRecommendedProducts() {
      this.apiStatus.getMyRecommendations.loading = true;
      this.apiStatus.getMyRecommendations.fail = false;
      this.apiStatus.getPrice.fail = false;

      try {
        // gets product cover type for closed product flow (is null if it is not a closed product)
        const closedProductCoverType = this.getCoverTypeForClosedProduct();

        const policyHolder = findPolicyHolder(this.memberDetails, true, true);
        const policyHolderLHCABD = formatQuoteLHCABD(this.changeCoverData.step2.policyHolder, this.policyHolderState, this.changeCoverData.step2.policyHolder.existingLHCOverrule);
        const payload = {
          state: this.changeCoverData.step1.state,
          eligibility: {
            eligibilityType: this.changeCoverData.step1.eligibility,
          },
          composition: this.changeCoverEnteredData ? this.changeCoverEnteredData.step2.coverComposition : null,
          hospitalAndExtras:
            this.changeCoverData.step2.coverType === CoverTypeValue.HOSPITAL_AND_EXTRAS
              ? closedProductCoverType || this.changeCoverData.step2.coverType
              : this.changeCoverData.step2.coverType,
          keepProduct: this.changeCoverData.step2.coverType === CoverTypeValue.HOSPITAL_AND_EXTRAS ? closedProductCoverType != null : false,
          medicalNeedClinical: this.convertListToString(this.changeCoverData.step3.specificTreatmentCategory),
          medicalNeedExtras: this.convertListToString(this.changeCoverData.step3.specificTreatmentModality),
          excludePregnancy:
            this.changeCoverData.step3.coverReason === 'planningFamily' ||
            this.changeCoverData.step3.specificTreatmentCategory.some((obj) => {
              return obj.returnValue.toLowerCase().includes('pregnancy');
            })
              ? false
              : this.changeCoverData.step3.noPregnancy,
          coverReason: this.changeCoverData.step3.coverReason,
          startDate: this.changeCoverData.step1.startDate,
          mainPolicyHolder: {
            firstName: policyHolder?.firstName,
            middleName: policyHolder?.middleName,
            lastName: policyHolder?.lastName,
            dateOfBirth: policyHolder?.dob,
            email: policyHolder?.email,
            ageBasedDiscount: !isEmpty(policyHolderLHCABD?.abd) ? { ...policyHolderLHCABD?.abd } : null,
            lifetimeHealthCoverOverrule: !isEmpty(policyHolderLHCABD?.lhc) ? { ...policyHolderLHCABD?.lhc } : null,
            veteranCard: this.formatDummyVeteranCard(this.changeCoverData.step2.policyHolder.veteranCard),
            covered: this.changeCoverData.step2.composition !== CoverComposition.CHILDREN_ONLY,
          },
          partner: null,
          numberOfChildren:
            this.changeCoverData.step2.numberOfChildren && this.compositionIncludesChildren(this.changeCoverEnteredData?.step2.coverComposition)
              ? parseInt(this.changeCoverData.step2.numberOfChildren, 10)
              : 0,
          rebate: {
            rebateTier: this.changeCoverData.step2.rebateTier.toLowerCase() !== RebateTierCode.NONE.toLowerCase() ? this.changeCoverData.step2.rebateTier : null,
          },
          existingProducts: this.getExistingProducts(),
        };

        if (
          [CoverComposition.COUPLE, CoverComposition.FAMILY, CoverComposition.FAMILY_PLUS].includes(
            this.changeCoverEnteredData ? this.changeCoverEnteredData.step2.coverComposition : this.changeCoverData.step2.composition,
          )
        ) {
          const partnerLHCABD = this.changeCoverData.step2.partner.keepAdult
            ? formatQuoteLHCABD(this.changeCoverData.step2.partner, this.partnerHospitalState, this.changeCoverData.step2.partner.existingLHCOverrule)
            : formatQuoteLHCABD(this.changeCoverData.step2.partner.newPartner, this.partnerHospitalState, this.changeCoverData.step2.partner.newPartner.existingLHCOverrule);

          payload.partner = {
            dateOfBirth: formatFormDate(this.partnerDOB),
            veteranCard: this.formatDummyVeteranCard(
              this.changeCoverData.step2.partner.keepAdult ? this.changeCoverData.step2.partner.veteranCard : this.changeCoverData.step2.partner.newPartner.veteranCard,
            ),
            ageBasedDiscount: !isEmpty(partnerLHCABD?.abd) ? { ...partnerLHCABD?.abd } : null,
            lifetimeHealthCoverOverrule: !isEmpty(partnerLHCABD?.lhc) ? { ...partnerLHCABD?.lhc } : null,
            covered: this.changeCoverData.step2.composition !== CoverComposition.CHILDREN_ONLY,
          };
        }

        if (this.changeCoverData.step1.recentDischarge && this.changeCoverData.step1.eligibility === NeedsAnalysisEligibility.EX_SERVING) {
          payload.eligibility.dischargeDate = formatDate(subDays(new Date(getCurrentDate()), 1));
        }
        const recommendedProducts = await Services.getRecommendedProducts(payload);
        if (recommendedProducts.recommendations && recommendedProducts.existingProducts) {
          // If is a closed product, combine the new recommendations with old closed product
          if (closedProductCoverType && this.changeCoverData.step2.coverType === CoverTypeValue.HOSPITAL_AND_EXTRAS) {
            this.combineClosedProductAndRecommendation(recommendedProducts.recommendations, recommendedProducts.existingProducts, closedProductCoverType);
          }
          this.updateMyRecommendations(recommendedProducts);
          this.fullRecommendationList = this.getFullRecommendationList(recommendedProducts.recommendations, recommendedProducts.existingProducts);
          if (recommendedProducts.recommendations.length > 0) {
            // get list of hospital and extras features
            this.updateHospitalAndExtrasFeatures(this.fullRecommendationList);
            this.getInitialExcessValues();
            await this.getPrice(); // get the price of recommended product
          }
        } else {
          throw new DHLError(`Issue with the response: fetchRecommendedProducts`);
        }
      } catch (error) {
        this.apiStatus.getMyRecommendations.fail = true;
        logger.log(error.message ?? error);
      } finally {
        this.apiStatus.getMyRecommendations.loading = false;
      }
    },

    /**
     * Get the price of recommendations
     */
    async getPrice() {
      this.apiStatus.getPrice.loading = true;
      this.apiStatus.getPrice.fail = false;
      const policyHolder = findPolicyHolder(this.memberDetails);
      const policyHolderLHCABD = formatQuoteLHCABD(this.changeCoverData.step2.policyHolder, this.policyHolderState, this.changeCoverData.step2.policyHolder.existingLHCOverrule);

      try {
        // Payload for recommendations
        const recommendationsPayload = {
          quoteType: 'C',
          updateCRMQuote: false,
          quoteId: this.myRecommendations.recommendations ? this.myRecommendations.recommendations[0].quoteId : null, // Take first quoteId from fetchRecommendedProducts since CRM quotes are not updated
          memberNumber: this.memberNumber,
          composition: this.changeCoverEnteredData ? this.changeCoverEnteredData.step2.coverComposition : this.changeCoverData.step2.composition,
          startDate: this.changeCoverData.step1.startDate,
          state: this.changeCoverData.step1.state,
          frequency: this.paymentFrequency,
          paymentStartDate: this.changeCoverData.step1.startDate,
          contributionMethod: 'DD',
          mainPolicyHolder: {
            firstName: policyHolder?.firstName,
            middleName: policyHolder?.middleName,
            lastName: policyHolder?.lastName,
            dateOfBirth: policyHolder?.dob,
            email: policyHolder?.email,
            ageBasedDiscount: !isEmpty(policyHolderLHCABD?.abd) ? { ...policyHolderLHCABD?.abd } : null,
            lifetimeHealthCoverOverrule: !isEmpty(policyHolderLHCABD?.lhc) ? { ...policyHolderLHCABD?.lhc } : null,
            veteranCard: this.formatDummyVeteranCard(this.changeCoverData.step2.policyHolder.veteranCard),
            covered: this.changeCoverData.step2.composition !== CoverComposition.CHILDREN_ONLY,
          },
          partner: null,
          numberOfChildren:
            this.changeCoverData.step2.numberOfChildren && this.compositionIncludesChildren(this.changeCoverEnteredData?.step2.coverComposition)
              ? parseInt(this.changeCoverData.step2.numberOfChildren, 10)
              : 0,
          productList: this.getProductList(this.myRecommendations.recommendations, this.selectedExcessValues),
          eligibility: {
            eligibilityType: this.changeCoverData.step1.eligibility,
          },
        };

        if (this.changeCoverData.step1.recentDischarge && this.changeCoverData.step1.eligibility === NeedsAnalysisEligibility.EX_SERVING) {
          recommendationsPayload.eligibility.dischargeDate = formatDate(subDays(new Date(getCurrentDate()), 1));
        }

        // EAI only accepts non-NONE ("Do not apply a rebate") values from RebateTierCode enum
        if (this.changeCoverData.step2.rebateTier.toLowerCase() !== RebateTierCode.NONE.toLowerCase()) {
          recommendationsPayload.rebate = {
            rebateTier: this.changeCoverData.step2.rebateTier,
          };
        }

        if (
          [CoverComposition.COUPLE, CoverComposition.FAMILY, CoverComposition.FAMILY_PLUS].includes(
            this.changeCoverEnteredData ? this.changeCoverEnteredData.step2.coverComposition : this.changeCoverData.step2.composition,
          )
        ) {
          const partnerLHCABD = this.changeCoverData.step2.partner.keepAdult
            ? formatQuoteLHCABD(this.changeCoverData.step2.partner, this.partnerHospitalState, this.changeCoverData.step2.partner.existingLHCOverrule)
            : formatQuoteLHCABD(this.changeCoverData.step2.partner.newPartner, this.partnerHospitalState, this.changeCoverData.step2.partner.newPartner.existingLHCOverrule);

          recommendationsPayload.partner = {
            dateOfBirth: formatFormDate(this.partnerDOB),
            veteranCard: this.formatDummyVeteranCard(
              this.changeCoverData.step2.partner.keepAdult ? this.changeCoverData.step2.partner.veteranCard : this.changeCoverData.step2.partner.newPartner.veteranCard,
            ),
            ageBasedDiscount: !isEmpty(partnerLHCABD?.abd) ? { ...partnerLHCABD?.abd } : null,
            lifetimeHealthCoverOverrule: !isEmpty(partnerLHCABD?.lhc) ? { ...partnerLHCABD?.lhc } : null,
            covered: this.changeCoverData.step2.composition !== CoverComposition.CHILDREN_ONLY,
          };
        }

        // Get price for recommended products
        const recommendationsPrice = await Services.getPrice(recommendationsPayload);

        if (recommendationsPrice.productPrices) {
          const recommendationListWithPrices = cloneDeep(this.fullRecommendationList);

          // sorts the response of products to be the same order as the payload product list
          const sortedPriceList = sortListBasedOnAnotherList(recommendationsPrice.productPrices, recommendationsPayload.productList, 'productCodes');
          // Add price and discounts for current product to the array
          const fullPriceList = sortedPriceList.concat({
            discounts: this.membershipPolicy.premiumInformation.discounts ? { ...this.membershipPolicy.premiumInformation.discounts } : {},
            calculationResultSetTotalResult: Number(this.membershipPolicy.premiumInformation.currentOngoingPremium),
          });

          // add price to recommendation list
          fullPriceList.forEach((productPrice, index) => {
            recommendationListWithPrices[index].price = productPrice.calculationResultSetTotalResult;
            recommendationListWithPrices[index].discountsAndLoadings = productPrice.discounts;
          });
          this.fullRecommendationList = recommendationListWithPrices;
        } else {
          throw new DHLError(`Issue with the response: getPrice`, recommendationsPrice);
        }
      } catch (error) {
        this.apiStatus.getPrice.fail = true;
        logger.log(error);
      } finally {
        this.apiStatus.getPrice.loading = false;
      }
    },

    /**
     * Returns the spouse's birthday
     */
    getSpouseBirthdate() {
      if (this.memberDetails) {
        const spouse = findSpouse(this.memberDetails);
        if (spouse) {
          return spouse.dob;
        }
      }
      return null;
    },

    /**
     * Combines the recommended product list and existing product into one recommendation list
     * @param {array} recommendedProducts recommendation list of objects (max of two values). Each object should contain products list
     * @param {object} existingProducts existing product object with properties 'existingProductName', 'products'
     * @returns Array with combined list or empty array
     */
    getFullRecommendationList(recommendedProducts, existingProducts) {
      if (recommendedProducts && existingProducts) {
        const recommendations = cloneDeep(recommendedProducts);
        recommendations.forEach((recommendation) => {
          recommendation.composition = this.changeCoverEnteredData ? this.changeCoverEnteredData.step2.coverComposition : this.changeCoverData.step2.composition;
          recommendation.coverType = this.changeCoverData.step2.coverType;
        });

        if (existingProducts) {
          // Existing Products don't recieve productExcess from CRM, use current excess if Hosp or Package product
          existingProducts.products.forEach((product) => {
            const isHospOrPackage = product.productType.toLowerCase() === ProductType.HOSPITAL_ONLY || product.productType.toLowerCase() === ProductType.PACKAGE;
            if (!product.productExcess && isHospOrPackage) {
              product.productExcess = this.currentExcess.toString();
            }
          });

          recommendations.push({
            recommendationName: existingProducts.existingProductName,
            products: existingProducts.products,
            composition: this.currentComposition,
            closedProductRetain: existingProducts.closedProductRetain,
            coverType: this.getCurrentProductType(existingProducts.products),
          });
        }

        recommendations.forEach((recommendation) => {
          recommendation.products.forEach((product) => {
            if (product.productExcess) {
              if (product.productExcess.includes(':')) {
                product.productExcess = product.productExcess.split(':');
              } else if (!Array.isArray(product.productExcess)) {
                product.productExcess = [product.productExcess];
              }
            }
          });
        });
        return recommendations;
      }
      return [];
    },

    /**
     * Update hopsital, extras and flexi feature arrays with those from recommendation list
     * @param {array} fullRecommendationList recommendation list of objects. Each object should contain products list
     */
    updateHospitalAndExtrasFeatures(fullRecommendationList) {
      this.hospitalNeeds = [];
      this.extrasNeeds = [];
      this.flexiFeatures = [];
      // Loop through one of the recommendations, since all recommendations will have the same list of clinical categories and extras modalities.
      const foundProductTypes = [];
      fullRecommendationList.forEach((recommendation) => {
        recommendation.products.forEach((product) => {
          // if product type is not found yet
          if (!foundProductTypes.includes(product.productType)) {
            // for package products, go through all products and add to list based on product type (hospital, extras, flexi)
            if (product.productType.toLowerCase() === ProductType.PACKAGE && foundProductTypes.length < 2) {
              foundProductTypes.push(ProductType.PACKAGE);
              product.productProperties.forEach((feature) => {
                if (!foundProductTypes.includes(feature.propertyType)) {
                  if (feature.propertyType.toLowerCase() === ProductType.HOSPITAL_ONLY) {
                    this.hospitalNeeds.push(feature);
                  } else if (feature.flexiLimit) {
                    this.flexiFeatures.push(feature);
                  } else {
                    this.extrasNeeds.push(feature);
                  }
                }
              });
            } else if (product.productType.toLowerCase() === ProductType.HOSPITAL_ONLY && !foundProductTypes.includes(ProductType.HOSPITAL_ONLY)) {
              foundProductTypes.push(ProductType.HOSPITAL_ONLY);
              this.hospitalNeeds = this.hospitalNeeds.concat(product.productProperties);
            } else if (product.productType.toLowerCase() === ProductType.EXTRAS_ONLY && !foundProductTypes.includes(ProductType.EXTRAS_ONLY)) {
              foundProductTypes.push(ProductType.EXTRAS_ONLY);
              product.productProperties.forEach((feature) => {
                // extras includes both flexi and extras. flexi features are denoted with `flexLimit = true`
                if (feature.flexiLimit) {
                  this.flexiFeatures.push(feature);
                } else {
                  this.extrasNeeds.push(feature);
                }
              });
            }
          }
        });
      });

      // move flexi feature extras to the end of list (to be viewed with remaining flexi features)
      const indexOfFlexi = this.extrasNeeds.findIndex((extrasProduct) => {
        return extrasProduct.propertyName === 'Flexi-limit';
      });

      if (indexOfFlexi !== -1) {
        this.extrasNeeds.push(this.extrasNeeds.splice(indexOfFlexi, 1)[0]);
      }

      // remove duplicate property names (so that hospital/extras tables don't have duplicate rows)
      this.hospitalNeeds = this.removeDuplicatePropertyNames(this.hospitalNeeds);
      this.extrasNeeds = this.removeDuplicatePropertyNames(this.extrasNeeds);
      this.flexiFeatures = this.removeDuplicatePropertyNames(this.flexiFeatures);
    },

    removeDuplicatePropertyNames(arr) {
      const uniquePropertyNames = Array.from(
        new Set(
          arr.map((a) => {
            return a.propertyName;
          }),
        ),
      );
      return uniquePropertyNames.map((propertyName) => {
        return arr.find((a) => {
          return a.propertyName === propertyName;
        });
      });
    },

    /**
     * Combine the current closed product with a new open product from recommendation
     * @param {array} recommendationsList recommendation list of objects. Each object should contain recommendationName
     * @param {object} existingProducts existing product with a products list property
     * @param {string} productTypeChanged the product type that is being changed/reviewed
     */
    combineClosedProductAndRecommendation(recommendationsList, existingProducts, productTypeChanged) {
      let productRetained;
      if (productTypeChanged !== CoverTypeValue.HOSPITAL_AND_EXTRAS) {
        productRetained = productTypeChanged === CoverTypeValue.HOSPITAL_ONLY ? CoverTypeValue.EXTRAS_ONLY : CoverTypeValue.HOSPITAL_ONLY;
      } else {
        productRetained = 'None';
      }
      const getExistingProductKept = existingProducts.products.find((product) => {
        return product.productType.toLowerCase() === productRetained;
      });
      // Update recommendation name for both hospital and extras
      // DE2E-12455: Update to retain existing kept products from CRM and avoid including duplicate product codes in recommendations
      if (getExistingProductKept) {
        recommendationsList.forEach((recommendation) => {
          if (productRetained === CoverTypeValue.HOSPITAL_ONLY) {
            recommendation.recommendationName = `${getExistingProductKept.productName} & ${recommendation.recommendationName}`;
          } else {
            recommendation.recommendationName = `${recommendation.recommendationName} & ${getExistingProductKept.productName}`;
          }
          recommendation.closedProductRetain = `Retained closed ${productRetained} product`;
          recommendation.disableExcess = this.changeCoverData.step2.reviewCover === CoverTypeValue.EXTRAS_ONLY;

          // since closed hospital product excess values cannot be changed, only keep the current selected excess in the excess list
          recommendation.products.forEach((product) => {
            if (product.productType.toLowerCase() === CoverTypeValue.HOSPITAL_ONLY) {
              // ensure that the only excess amount added in these recommendations is the currently selected excess
              if (recommendation.disableExcess) {
                product.productExcess = this.currentExcess.toString();
              }
            }
          });
        });

        // Update text for recommendations page to shoe retained product
        if (this.closedHospitalProduct && this.closedExtrasProduct) {
          existingProducts.closedProductRetain = `Retained current hospital and current extras product`;
        } else if (this.closedExtrasProduct) {
          if (existingProducts.products.length > 1) {
            // if more than one product there must be another open hospital product
            existingProducts.closedProductRetain = `Retained hospital and current extras product`;
          } else {
            existingProducts.closedProductRetain = `Retained current extras product`;
          }
        } else if (this.closedHospitalProduct) {
          if (existingProducts.products.length > 1) {
            // if more than one product there must be another open extras product
            existingProducts.closedProductRetain = `Retained current hospital and extras product`;
          } else {
            existingProducts.closedProductRetain = `Retained current hospital product`;
          }
        }
      }
    },

    /**
     * Gets the cover type from the list of products given
     * - If has more than one product, then it is a hospital and extras product
     * - If has only one product, check if package, hospital only, or extras only
     */
    getCurrentProductType(productList) {
      if (productList.length > 0) {
        if (productList.length > 1) {
          return CoverTypeValue.HOSPITAL_AND_EXTRAS;
        }
        if (productList.length === 1) {
          if (productList[0].productType.toLowerCase() === ProductType.PACKAGE) {
            return CoverTypeValue.HOSPITAL_AND_EXTRAS;
          }
          return productList[0].productType.toLowerCase();
        }
      }
      return null;
    },

    handleNextButton() {
      this.currentPage += 1;
      this.updateStep(this.currentPage);
    },

    /**
     * Updates the emitted data from components to update response
     */
    updateVuexFormResponse(data) {
      const pages = cloneDeep(this.steps);
      const formResponseStepRange = Object.values(pages).map((obj) => {
        return obj.pageNumber;
      });
      if (this.currentPage >= Math.min(...formResponseStepRange) && this.currentPage <= Math.max(...formResponseStepRange)) {
        switch (this.currentPage) {
          case this.steps.MY_DETAILS.pageNumber:
            this.steps.MY_DETAILS.isInvalid = data.isInvalid;
            this.updateMyDetails(data.response);
            break;

          case this.steps.MY_COVER.pageNumber:
            this.steps.MY_COVER.isInvalid = data.isInvalid;
            this.updateMyCover(data.response);
            break;

          case this.steps.MY_NEEDS.pageNumber:
            this.steps.MY_NEEDS.isInvalid = data.isInvalid;
            this.updateMyNeeds(data.response);
            break;

          case this.pages.MY_RECOMMENDATIONS.pageNumber:
            this.pages.MY_RECOMMENDATIONS.isInvalid = data.isInvalid;
            break;

          default:
            break;
        }
      }
    },

    /**
     * Takes a Veteran Card object captured in My Cover and formats a consistent payload for Recommendations and Pricing calculations
     */
    formatDummyVeteranCard(capturedDetails) {
      if (!capturedDetails) {
        return null;
      }

      return {
        hasVeteranCard: capturedDetails.haveVeteranCard ?? false,
        cardType: capturedDetails.haveVeteranCard ? capturedDetails.veteranCardType : null,
        acceptedConditions: capturedDetails.veteranCardType?.toLowerCase() === VeteranCardTypeOptions.VETERAN_WHITE_CARD.toLowerCase() ? ['DVAC3'] : [],
      };
    },
  },
};
</script>

<style lang="scss" scoped>
.my-recommendations {
  width: 100%;
  @include tablet-and-below {
    padding: unset;
    background: none;
    box-shadow: unset;
  }
}

.col-12 {
  &.no-gutter {
    margin-bottom: rem(8);
  }
}

.required-indicator {
  font-size: font-size(-2);

  span {
    font-weight: $BOLD;
    color: $CRIMSON;
  }
}

.aux-icons {
  max-height: 16px;
  max-width: 16px;
}

.recommendations-legend {
  margin: 0 20px 56px;
  display: flex;
  padding: 36px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 24px;
  border-radius: 16px;
  background: var(--White, #fff);
  box-shadow: 0px 6px 12px 0px rgba(0, 0, 0, 0.2);

  p {
    margin-bottom: 0;
  }

  @include mobile {
    margin: 56px 0;
  }
}

.icons-row {
  gap: 8px 32px;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  margin: 0;
}

.icon-key {
  align-items: center;
  display: flex;

  p {
    margin: 0 0 0 16px;
  }
}
</style>
